import { Box, Grid, Typography, Divider, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { isVisibleInPortal } from './positionStatus';

const InfoRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>{label}</Typography>
      <Typography variant="body2" sx={{ mt: 0.25 }}>{value}</Typography>
    </Grid>
  );
};

const AtsPositionInfoTab = ({ position, formatSalario }) => {
  const { t } = useTranslation();

  // Condiciones económicas del encargo (uso interno)
  const formatMonto = (valor) => {
    if (!valor) return null;
    const currency = position?.condicionesPago?.currency || '';
    return `${currency} ${Number(valor).toLocaleString()}`.trim();
  };

  // "30% — Monto: CLP 600.000"
  const formatTramo = (porcentaje) => {
    if (porcentaje == null || porcentaje === '') return null;
    const precio = parseFloat(position?.condicionesPago?.precio);
    if (!precio) return `${porcentaje}%`;
    const monto = formatMonto((precio * parseFloat(porcentaje)) / 100);
    return `${porcentaje}% — ${t('ats.positions.wizard.condicionesPago.montoTramo', { monto })}`;
  };

  const formatDate = (fecha) => {
    if (!fecha) return null;
    return new Date(fecha).toLocaleDateString('es-CL', {
      day: '2-digit', month: 'long', year: 'numeric',
    });
  };

  // Bonos [{ nombre, descripcion, monto }] y beneficios [{ nombre, descripcion }]
  const bonos = Array.isArray(position?.bonos)
    ? position.bonos.filter((b) => b && b.nombre) : [];
  const beneficios = Array.isArray(position?.beneficios)
    ? position.beneficios.filter((b) => b && typeof b === 'object' && b.nombre) : [];
  // Competencias predeterminadas del cargo [{ competencyId, competencyName }]
  const competencias = Array.isArray(position?.competencias)
    ? position.competencias.filter((c) => c && typeof c === 'object' && c.competencyName) : [];
  const salarioCurrency = position?.salarioRango?.currency || '';

  // Lista de ítems con nombre en negrita y descripción/monto al lado
  const ItemList = ({ label, items, showMonto = false }) => (
    <Grid item xs={12}>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>{label}</Typography>
      <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2.5 }}>
        {items.map((it, i) => (
          <Typography component="li" variant="body2" key={i}>
            <strong>{it.nombre}</strong>
            {showMonto && it.monto ? ` — ${salarioCurrency} ${Number(it.monto).toLocaleString()}`.trimEnd() : ''}
            {it.descripcion ? ` — ${it.descripcion}` : ''}
          </Typography>
        ))}
      </Box>
    </Grid>
  );

  return (
    <Grid container spacing={3}>

      <Grid item xs={12}>
        <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
          {t('ats.positions.wizard.basicInfo')}
        </Typography>
        <Divider sx={{ mt: 0.5, mb: 2 }} />
      </Grid>

      <InfoRow label={t('ats.positions.wizard.positionName')} value={position?.name} />
      <InfoRow label={t('ats.positions.wizard.codigoRef')} value={position?.codigoRef} />
      <InfoRow label={t('ats.positions.wizard.industry')} value={position?.industria} />
      <InfoRow label={t('ats.positions.wizard.jerarquia')} value={position?.jerarquia} />
      <InfoRow label={t('ats.positions.wizard.area')} value={position?.area} />
      <InfoRow label={t('ats.positions.wizard.subArea')} value={position?.subArea} />
      <InfoRow label={t('ats.positions.wizard.numVacantes')} value={position?.numPosiciones?.toString()} />
      <InfoRow label={t('ats.positions.wizard.motivoBusqueda')} value={position?.motivoBusqueda} />
      <InfoRow
        label={t('ats.positions.wizard.prioridad')}
        value={position?.prioridad ? t(`ats.positions.wizard.options.prioridad.${position.prioridad}`) : null}
      />
      <InfoRow
        label={t('ats.positions.wizard.tipo')}
        value={position?.tipo ? t(`ats.positions.wizard.options.tipo.${position.tipo}`) : null}
      />

      <Grid item xs={12} sx={{ mt: 1 }}>
        <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
          {t('ats.positions.wizard.tabs.logistics')}
        </Typography>
        <Divider sx={{ mt: 0.5, mb: 2 }} />
      </Grid>

      <InfoRow
        label={t('ats.positions.wizard.modalidad')}
        value={position?.modalidad ? t(`ats.positions.wizard.options.modalidad.${position.modalidad}`) : null}
      />
      <InfoRow label={t('ats.positions.wizard.country')} value={position?.ubicacion?.country} />
      <InfoRow label={t('ats.positions.wizard.province')} value={position?.ubicacion?.province} />
      <InfoRow label={t('ats.positions.wizard.city')} value={position?.ubicacion?.city} />
      <InfoRow label={t('ats.positions.wizard.fechaApertura')} value={formatDate(position?.fechaApertura)} />
      <InfoRow label={t('ats.positions.wizard.fechaCierre')} value={formatDate(position?.fechaCierre)} />
      <InfoRow
        label={t('ats.positions.wizard.tipoBusqueda')}
        value={position?.tipoBusqueda ? t(`ats.positions.wizard.options.tipoBusqueda.${position.tipoBusqueda}`) : null}
      />
      {/* "Visible en portal" (reemplaza al antiguo "Tipo de publicación") */}
      <InfoRow
        label={t('ats.positions.wizard.captacionBot.portalSwitchLabel')}
        value={position ? (isVisibleInPortal(position) ? t('yes') : t('no')) : null}
      />

      {(formatSalario(position?.salarioRango) || bonos.length > 0) && (
        <>
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
              {t('ats.positions.wizard.tabs.salary')}
            </Typography>
            <Divider sx={{ mt: 0.5, mb: 2 }} />
          </Grid>
          <InfoRow
            label={position?.salarioRango?.tipo === 'unico'
              ? t('ats.positions.wizard.salarioMonto')
              : t('ats.positions.wizard.salary')}
            value={formatSalario(position?.salarioRango)}
          />
          <InfoRow
            label={t('ats.positions.wizard.salarioVisible')}
            value={position?.salarioRango?.visible ? t('yes') : t('no')}
          />
          {bonos.length > 0 && (
            <ItemList label={t('ats.positions.wizard.bonos.title')} items={bonos} showMonto />
          )}
        </>
      )}

      {/* Condiciones económicas del encargo. Dato interno: el candidato nunca
          lo ve (los endpoints públicos usan whitelist de campos). */}
      {position?.condicionesPago?.precio && (
        <>
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
              {t('ats.positions.wizard.condicionesPago.title')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('ats.positions.wizard.condicionesPago.internalNote')}
            </Typography>
            <Divider sx={{ mt: 0.5, mb: 2 }} />
          </Grid>
          <InfoRow
            label={t('ats.positions.wizard.condicionesPago.precio')}
            value={formatMonto(position.condicionesPago.precio)}
          />
          <InfoRow
            label={t('ats.positions.wizard.condicionesPago.kickoff')}
            value={formatTramo(position.condicionesPago.porcentajeKickoff)}
          />
          <InfoRow
            label={t('ats.positions.wizard.condicionesPago.cierre')}
            value={formatTramo(position.condicionesPago.porcentajeCierre)}
          />
        </>
      )}

      {(position?.descripcion || position?.responsabilidades || position?.perfil || beneficios.length > 0 || competencias.length > 0) && (
        <>
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
              {t('ats.positions.wizard.tabs.description')}
            </Typography>
            <Divider sx={{ mt: 0.5, mb: 2 }} />
          </Grid>

          {position?.descripcion && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {t('ats.positions.wizard.descripcion')}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>
                {position.descripcion}
              </Typography>
            </Grid>
          )}

          {position?.responsabilidades && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {t('ats.positions.wizard.responsabilidades')}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>
                {position.responsabilidades}
              </Typography>
            </Grid>
          )}

          {position?.perfil && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {t('ats.positions.wizard.perfil')}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>
                {position.perfil}
              </Typography>
            </Grid>
          )}

          {position?.conocimientosExcluyentes && (
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {t('ats.positions.wizard.conocimientosExcluyentes')}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>
                {position.conocimientosExcluyentes}
              </Typography>
            </Grid>
          )}

          {position?.conocimientosDeseables && (
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {t('ats.positions.wizard.conocimientosDeseables')}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>
                {position.conocimientosDeseables}
              </Typography>
            </Grid>
          )}

          {competencias.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {t('ats.positions.wizard.competencias')}
              </Typography>
              <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {competencias.map((c, i) => (
                  <Chip key={c.competencyId || i} label={c.competencyName} size="small" variant="outlined" />
                ))}
              </Box>
            </Grid>
          )}

          {beneficios.length > 0 && (
            <ItemList label={t('ats.positions.wizard.beneficios.title')} items={beneficios} />
          )}
        </>
      )}
    </Grid>
  );
};

export default AtsPositionInfoTab;